/*----------------------------------
- HOOK: USE PRODUCT SESSION
----------------------------------*/

/**
 * @docs docs/features/website-landing-experience
 * @fix  docs/fixes/2026-09-14-website-header-blind-to-members.md
 * @rule The first render is always the guest render. Identity arrives from the browser
 *       probe, never from SSR, because the SSR output is cached and shared.
 */

import React from 'react';

import type { CurrentUserSession } from '@uniquedomains/components/contracts/users/session';
import { isWorkspaceReadySession } from '@/client/services/conversion/handoff';
import {
    readCachedProductSession,
    readProductSession,
    type ProductSessionSnapshot,
} from '@/client/services/conversion/product/productSession';

/*----------------------------------
- TYPES
----------------------------------*/

export type ProductSessionStatus = 'pending' | 'anonymous' | 'member';
export type ProductSessionState = {
    status: ProductSessionStatus;
    session: CurrentUserSession | null;
};

/*----------------------------------
- HELPERS
----------------------------------*/

/**
 * A live session that has not finished onboarding stays `anonymous` here. Such a visitor
 * has an account but no workspace to open, and Start with my brief is the control that resumes
 * their flow: the conversion modal detects the live session at mount and continues it.
 * Showing them "Open app" would send them to a product that bounces them straight back
 * into setup.
 */
const resolveStatus = ({ session, settled }: ProductSessionSnapshot): ProductSessionStatus => {
    if (!settled) return 'pending';
    return isWorkspaceReadySession(session) ? 'member' : 'anonymous';
};

/*----------------------------------
- HOOK
----------------------------------*/

export default function useProductSession(productOrigin: string): ProductSessionState {
    /*
     * Seeded from the cache rather than from `null` so a client-side navigation renders
     * the member header immediately. On the first document the cache is empty, which is
     * what keeps the hydration render identical to the SSR guest markup.
     */
    const [snapshot, setSnapshot] = React.useState<ProductSessionSnapshot>(readCachedProductSession);

    React.useEffect(() => {
        let cancelled = false;

        void readProductSession(productOrigin).then((session) => {
            if (!cancelled) setSnapshot({ session, settled: true });
        });

        return () => {
            cancelled = true;
        };
    }, [productOrigin]);

    return { status: resolveStatus(snapshot), session: snapshot.session };
}
